import { useState, useEffect } from 'react';
import { Download, Smartphone, Monitor, Globe, Share, Plus } from 'lucide-react';
import { FaChrome } from "react-icons/fa";
import './InstallPage.css';

export default function InstallPage() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    setIsIOS(/iphone|ipad|ipod/.test(ua));
    setIsAndroid(/android/.test(ua));

    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      setIsInstalled(true);
      setDeferredPrompt(null);
    }

    setInstalling(false);
  };

  return (
    <div className="install-page fade-in">

      {/* Hero */}
      <div className="install-hero card">
        {/* ✅ Logo */}
        <img 
          src="/logo.png" 
          alt="GLA Exchange" 
          className="install-app-icon-img"
        />

        <h2>Install GLA Exchange</h2>

        <p className="text-muted" style={{ fontSize: '0.9rem', textAlign: 'center', maxWidth: 340 }}>
          Get the full app experience. Works on Android, iPhone, and desktop.
          Fast, offline-ready, and feels like a native app.
        </p>

        {isInstalled && (
          <div className="install-already-badge">
            ✅ Already installed on this device
          </div>
        )}
      </div>

      {/* Chrome / Android install */}
      {deferredPrompt && !isInstalled && (
        <div className="card install-section">
          <div className="install-section-header">
            <FaChrome size={22} className="install-section-icon" />
            <div>
              <h4>Install on this device</h4>
              <p className="text-muted" style={{ fontSize: '0.82rem' }}>
                Chrome, Edge or Android detected — one click install available.
              </p>
            </div>
          </div>

          <button
            className="btn btn-primary btn-full btn-lg"
            onClick={handleInstall}
            disabled={installing}
          >
            {installing
              ? <><div className="spinner" /> Installing...</>
              : <><Download size={18} /> Install App Now</>
            }
          </button>
        </div>
      )}

      {/* iOS */}
      {isIOS && !isInstalled && (
        <div className="card install-section">
          <div className="install-section-header">
            <Smartphone size={22} className="install-section-icon" />
            <div>
              <h4>Install on iPhone / iPad</h4>
              <p className="text-muted" style={{ fontSize: '0.82rem' }}>
                Follow these steps in Safari
              </p>
            </div>
          </div>

          <div className="install-steps">
            {[
              { icon: Share, step: '1', text: 'Tap the Share button in Safari' },
              { icon: Plus, step: '2', text: 'Tap "Add to Home Screen"' },
              { icon: Download, step: '3', text: 'Tap "Add"' },
            ].map(s => (
              <div key={s.step} className="install-step">
                <div className="install-step-num">{s.step}</div>
                <s.icon size={18} className="install-step-icon" />
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Android manual */}
      {isAndroid && !deferredPrompt && !isInstalled && (
        <div className="card install-section">
          <div className="install-section-header">
            <Smartphone size={22} className="install-section-icon" />
            <div>
              <h4>Install on Android</h4>
              <p className="text-muted" style={{ fontSize: '0.82rem' }}>
                Open in Chrome for best experience
              </p>
            </div>
          </div>

          <div className="install-steps">
            {[
              { step: '1', text: 'Open this page in Chrome' },
              { step: '2', text: 'Tap the 3-dot menu (⋮)' },
              { step: '3', text: 'Tap "Add to Home screen"' },
              { step: '4', text: 'Tap "Add"' },
            ].map(s => (
              <div key={s.step} className="install-step">
                <div className="install-step-num">{s.step}</div>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Desktop */}
      <div className="card install-section">
        <div className="install-section-header">
          <Monitor size={22} className="install-section-icon" />
          <div>
            <h4>Install on Desktop</h4>
            <p className="text-muted" style={{ fontSize: '0.82rem' }}>
              Chrome, Edge, or Brave
            </p>
          </div>
        </div>

        <div className="install-steps">
          {[
            { step: '1', text: 'Open in Chrome/Edge' },
            { step: '2', text: 'Click install icon in address bar' },
            { step: '3', text: 'Click "Install"' },
          ].map(s => (
            <div key={s.step} className="install-step">
              <div className="install-step-num">{s.step}</div>
              <p>{s.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Benefits */}
      <div className="card install-benefits">
        <h4 style={{ marginBottom: 'var(--space-4)' }}>Why install?</h4>

        <div className="install-benefits-grid">
          {[
            { emoji: '⚡', title: 'Faster', desc: 'Loads instantly' },
            { emoji: '🔔', title: 'Notifications', desc: 'Stay updated' },
            { emoji: '📱', title: 'Fullscreen', desc: 'More screen space' },
            { emoji: '🔒', title: 'Secure', desc: 'Same as website' },
          ].map(b => (
            <div key={b.title} className="install-benefit-item">
              <span className="install-benefit-emoji">{b.emoji}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{b.title}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{b.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}